import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';

import { ButtonComponent } from '@libs/front/components/button/button.component';
import { DataTableComponent, TableCellDirective, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { ButtonListComponent } from '@libs/front/components/button-list/button-list.component';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { DrillActivityService } from 'src/app/services/drill-activity.service';
import { DrillSessionApiDto, DrillingSession } from 'src/app/shared/models/drilling.models';
import {
  BlastDrillingSessionFormDialogComponent,
  SessionFormDialogData,
} from 'src/app/components/blast-drilling-panel/blast-drilling-session-form-dialog.component';

import { BlastHolesStateService } from '../../blast-holes-state.service';
import { readColumnKeys, storeColumnKeys } from '../../shared/column-preferences';

/** One table row; keys match the column keys. Numeric cells stay numbers so the table sorts them. */
interface SessionRow {
  id:            string;
  number:        number;
  rig:           string;
  operator:      string;
  shift:         string;
  /** ISO date `YYYY-MM-DD` (site local), formatted by the cell template */
  date:          string;
  shiftDuration: number;
  engineHours:   number;
  pauseHours:    number;
  /** Holes of the blast drilled during the session */
  holes:         number;
  drilledMeters: number;
  details:       string;
}

const COLUMNS: readonly TableColumn[] = [
  { key: 'number',        label: 'HOLES_TAB.DRILLING_COL_NUMBER' },
  { key: 'rig',           label: 'HOLES_TAB.DRILLING_COL_RIG' },
  { key: 'operator',      label: 'HOLES_TAB.DRILLING_COL_OPERATOR' },
  { key: 'shift',         label: 'HOLES_TAB.DRILLING_COL_SHIFT' },
  { key: 'date',          label: 'HOLES_TAB.DRILLING_COL_DATE' },
  { key: 'shiftDuration', label: 'HOLES_TAB.DRILLING_COL_SHIFT_DURATION' },
  { key: 'engineHours',   label: 'HOLES_TAB.DRILLING_COL_ENGINE_HOURS' },
  { key: 'pauseHours',    label: 'HOLES_TAB.DRILLING_COL_PAUSE' },
  { key: 'holes',         label: 'HOLES_TAB.DRILLING_COL_HOLES' },
  { key: 'drilledMeters', label: 'HOLES_TAB.DRILLING_COL_DRILLED' },
  { key: 'details',       label: 'HOLES_TAB.DRILLING_COL_DETAILS', sortable: false },
];

/** Columns shown by default (Figma); the picker can add engine hours, pause and holes. */
const DEFAULT_COLUMN_KEYS = ['number', 'rig', 'operator', 'shift', 'date', 'shiftDuration', 'drilledMeters', 'details'];

/** Marker colour of the holes drilled during the selected session. */
const SESSION_HOLE_COLOR = '#6C1D45';

const RING_RADIUS = 12;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Holes › Drilling session view (no legend).
 * KPI tiles (drilled length vs planned, engine hours, drilling rate), "Add drill session" and the
 * session table ported from the legacy `blast-drilling-panel`. Selecting a session highlights on
 * the map the holes drilled during the session; "Details" opens the legacy standalone
 * session form dialog to edit it.
 * Design source : Figma 2544:671736
 */
@Component({
  selector:        'app-holes-drilling-view',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    DecimalPipe,
    TranslateModule,
    ButtonComponent,
    DataTableComponent,
    TableCellDirective,
    ButtonListComponent,
  ],
  templateUrl: './holes-drilling-view.component.html',
  styleUrls:   ['./holes-drilling-view.component.scss'],
})
export class HolesDrillingViewComponent {
  protected readonly state = inject(BlastHolesStateService);
  private readonly userService   = inject(UtilisateurService);
  private readonly drillActivity = inject(DrillActivityService);
  private readonly translate     = inject(TranslateService);
  private readonly dialog        = inject(MatDialog);
  private readonly destroyRef    = inject(DestroyRef);

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  // ── Sessions ──────────────────────────────────────────────────────────────

  /** View-model sessions in the legacy default order: date, shift, rig. */
  protected readonly sessions = computed<DrillingSession[]>(() => {
    const config   = this.state.shiftConfig();
    const timezone = this.userService.currentSite()?.timezone;
    return (this.state.drillSessions() ?? [])
      .map(dto => this.drillActivity.mapApiDtoToSession(dto, config, timezone))
      .sort((a, b) =>
        a.date.localeCompare(b.date) || a.shiftIndex - b.shiftIndex || a.equipmentLabel.localeCompare(b.equipmentLabel));
  });

  private readonly sessionsById = computed(() => new Map(this.sessions().map(session => [session.id, session])));

  /**
   * Session id → publicGuids of the blast holes drilled during it, from the hole ↔ session link
   * recorded by the API. A hole's drilling timestamp cannot be used: it may fall outside the
   * session that drilled it (e.g. after its end) and then lands in another session.
   */
  private readonly holesBySession = computed<ReadonlyMap<string, ReadonlySet<string>>>(() => {
    const result = new Map<string, Set<string>>();
    const blastHoles = new Map(this.state.holes().map(hole => [hole.publicGuid.toLowerCase(), hole.publicGuid]));

    for (const session of this.sessions()) {
      const set = new Set<string>();
      for (const holeId of session.rawDto?.holeIds ?? []) {
        const publicGuid = blastHoles.get(holeId.toLowerCase());
        if (publicGuid) set.add(publicGuid);
      }
      result.set(session.id, set);
    }
    return result;
  });

  // ── KPIs ──────────────────────────────────────────────────────────────────

  /** Planned drilled length: sum of the design lengths of the blast holes. */
  protected readonly targetMeters = computed(() =>
    Math.round(this.state.holes().reduce((sum, hole) => sum + (hole.Geometry?.Design?.length ?? 0), 0)),
  );

  protected readonly totalDrilled = computed(() =>
    this.sessions().reduce((sum, session) => sum + session.drilledMeters, 0),
  );

  protected readonly totalEngineHours = computed(() =>
    this.sessions().reduce((sum, session) => sum + session.engineHours, 0),
  );

  protected readonly drillingRate = computed(() => {
    const hours = this.totalEngineHours();
    return hours > 0 ? this.totalDrilled() / hours : 0;
  });

  protected readonly progressPercent = computed(() => {
    const target = this.targetMeters();
    return target > 0 ? Math.min(100, (this.totalDrilled() / target) * 100) : 0;
  });

  protected readonly ringDash = computed(() => {
    const ratio = this.progressPercent() / 100;
    return `${(ratio * RING_CIRCUMFERENCE).toFixed(2)} ${RING_CIRCUMFERENCE.toFixed(2)}`;
  });

  protected readonly ringRadius = RING_RADIUS;

  // ── Table ─────────────────────────────────────────────────────────────────

  protected readonly allColumns = COLUMNS as TableColumn[];
  /** User's column choice, remembered in the browser (see shared/column-preferences.ts) */
  protected readonly visibleColumnKeys = signal<Set<string>>(readColumnKeys('drilling', COLUMNS, DEFAULT_COLUMN_KEYS));
  protected readonly visibleColumns = computed(() =>
    this.allColumns.filter(column => this.visibleColumnKeys().has(column.key)),
  );

  protected onColumnsChange(keys: Set<string>): void {
    this.visibleColumnKeys.set(keys);
    storeColumnKeys('drilling', keys);
  }

  protected readonly rows = computed<SessionRow[]>(() => {
    this.lang();
    const holesBySession = this.holesBySession();
    const details = this.translate.instant('HOLES_TAB.DRILLING_DETAILS');
    return this.sessions().map((session, index) => ({
      id:            session.id,
      number:        index + 1,
      rig:           session.equipmentLabel,
      operator:      session.operatorLabel,
      shift:         this.shiftLabel(session.shiftIndex),
      date:          session.date,
      shiftDuration: session.shiftDurationHours,
      engineHours:   session.engineHours,
      pauseHours:    session.pauseHours,
      holes:         holesBySession.get(session.id)?.size ?? 0,
      drilledMeters: session.drilledMeters,
      details,
    }));
  });

  constructor() {
    // Sessions of the blast and the site shift configuration (labels / start hours)
    effect(() => {
      if (this.state.blastId()) untracked(() => void this.state.loadDrillSessions());
    });
    effect(() => {
      const siteId = this.userService.currentSite()?.siteId;
      if (siteId) untracked(() => void this.state.loadShiftConfig(siteId));
    });

    // Highlight the holes of the selected session on the map; restore the status icons otherwise
    effect(() => {
      const selected = this.state.selectedSessionId();
      const inSession = selected ? this.holesBySession().get(selected) : undefined;
      if (!selected || !inSession) {
        this.state.holeColors.set(null);
        return;
      }
      const colors = new Map<string, string | null>();
      for (const hole of this.state.holes()) {
        colors.set(hole.publicGuid, inSession.has(hole.publicGuid) ? SESSION_HOLE_COLOR : null);
      }
      this.state.holeColors.set(colors);
    });
    this.destroyRef.onDestroy(() => this.state.holeColors.set(null));
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  protected onRowClick(row: SessionRow): void {
    this.state.toggleSession(row.id);
  }

  protected onAddSession(): void {
    this.openSessionDialog();
  }

  protected onDetails(row: SessionRow, event: Event): void {
    event.stopPropagation();
    this.openSessionDialog(this.sessionsById().get(row.id)?.rawDto);
  }

  protected retry(): void {
    void this.state.loadDrillSessions(true);
  }

  // ── Formatting ────────────────────────────────────────────────────────────

  /** Always in whole minutes (`150 min`), like the session form: the API stores hours. */
  protected formatPause(hours: number): string {
    return `${Math.round(hours * 60)} min`;
  }

  protected sessionPercent(drilledMeters: number): number {
    const target = this.targetMeters();
    return target > 0 ? Math.min(100, Math.round((drilledMeters / target) * 100)) : 0;
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  /** Shift label from the site configuration, translated when a key exists, else capitalised. */
  private shiftLabel(shiftIndex: number): string {
    const label = this.state.shiftConfig().shiftLabels[shiftIndex];
    if (!label) return this.translate.instant('HOLES_TAB.DRILLING_SHIFT_N', { n: shiftIndex + 1 });
    const key = 'HOLES_TAB.DRILLING_SHIFT_' + label.toUpperCase().replace(/[^A-Z0-9]/g, '_');
    const translated = this.translate.instant(key);
    return translated === key ? label.charAt(0).toUpperCase() + label.slice(1) : translated;
  }

  private openSessionDialog(session?: DrillSessionApiDto): void {
    const blastId = this.state.blastId();
    const site = this.userService.currentSite();
    if (!blastId || !site?.siteId) return;

    const data: SessionFormDialogData = { blastId, siteId: site.siteId, session, timezone: site.timezone };
    this.dialog.open(BlastDrillingSessionFormDialogComponent, { data })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dto: DrillSessionApiDto | undefined) => {
        if (dto) void this.state.saveDrillSession(dto);
      });
  }
}
